import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import styled from "@emotion/styled";
import { Done, WarningAmber } from "@mui/icons-material";
import { Box } from "@mui/material";

import useLoading from "~common/hooks/useLoading";
import useQueryParams from "~common/hooks/useQueryParams";
import { LS_LOGIN_DATE } from "~constants";
import HistoryHelper from "~helpers/HistoryHelper";
import StorageHelper from "~helpers/StorageHelper";
import Utils from "~helpers/Utils";
import { getCurrentUser } from "~store/actions";
import { checkShopifyIntegration } from "~store/settings/eCommerce/actions";
import { selectUserInfo } from "~store/user/selectors";

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  position: absolute;
  width: 100%;
  height: calc(100%);
  padding: 30px 50px;
  @media (max-width: 600px) {
    padding: 30px 20px;
  }

  overflow-y: auto;
  svg {
    flex: 0 0 auto;
  }

  .message {
    text-align: center;
    font: normal normal 600 24px/34px MuseoModerno;
    color: ${(props) => props.theme.palette.primary.dark};
    &-countdown {
      font-size: 16px;
      color: ${(props) => props.theme.palette.secondary.main};
    }
  }

  .icon {
    font-size: 48px;
    margin-top: 8px;
  }
`;
const COUNTDOWN = 3;
const ShopifyCallback = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { code } = useQueryParams();
  const [loading, q] = useLoading(true);

  const [countDown, setCountDown] = useState(COUNTDOWN);
  const [checkStatus, setCheckStatus] = useState({
    is_connected: false,
    project_name: "",
    company_name: "",
    connected_project_id: 0,
    company_owner_name: "",
    is_project_connected: false,
    fetched: false,
  });
  const [treeDotsCount, setTreeDotsCount] = useState(0);
  const userInfo = useSelector(selectUserInfo);

  const { isConnectedToMyProject, isConnectedToAnotherProject } = useMemo(() => {
    const myProjectIds = userInfo?.id ? userInfo?.project_roles?.map((projectRole) => projectRole.project) : [];
    const isConnectedToMyProject =
      userInfo?.id && checkStatus?.is_project_connected
        ? myProjectIds?.some((projectId) => projectId === checkStatus?.connected_project_id)
        : false;
    const isConnectedToAnotherProject = userInfo?.id
      ? checkStatus?.is_project_connected && !isConnectedToMyProject
      : false;
    return {
      isConnectedToMyProject: !!isConnectedToMyProject,
      isConnectedToAnotherProject: !!isConnectedToAnotherProject,
    };
  }, [userInfo, checkStatus]);

  useEffect(() => {}, [dispatch, q]);

  useEffect(() => {
    q(
      dispatch(
        getCurrentUser({
          noThrow: true,
          noAlert: true,
        })
      )
    );
  }, [dispatch, q]);

  useEffect(() => {
    q(
      dispatch(
        checkShopifyIntegration({
          code,
        })
      )
    ).then((response) => {
      setCheckStatus(() => ({
        ...response,
        fetched: true,
      }));
    });
  }, [dispatch, q, code]);

  useEffect(() => {
    if (!loading && !userInfo?.id) {
      setCountDown(0);
      return;
    }
  }, [loading, userInfo]);

  useEffect(() => {
    if (!code || !checkStatus?.fetched) return;

    const interval = setInterval(() => {
      setCountDown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
        }
        return Math.max(prev - 1, 0);
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [code, checkStatus?.fetched]);

  useEffect(() => {
    if (countDown === 0 && !loading && !isConnectedToAnotherProject) {
      if (userInfo?.id) {
        let targetPathname = "/dashboard/setup/";
        // if (!userInfo.is_superuser && userInfo.project_roles?.length === 1) {
        //   const firstProject = userInfo.project_roles?.[0]?.project;

        //   targetPathname = `/dashboard/setup/${firstProject}`;
        // }

        HistoryHelper.push({
          pathname: targetPathname,
          search: Utils.qs({
            shopifyCode: code,
            shopifyInt: true,
            shopifyConnDone: isConnectedToMyProject,
            shopifyConnId: checkStatus?.connected_project_id,
          }),
        });
      } else {
        StorageHelper.get(LS_LOGIN_DATE).then((loginDate) => {
          const loginDateTime = Number(loginDate);
          const currentTime = new Date().getTime();
          const oneMonthInMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
          const isLoggedInLastMonth = currentTime - loginDateTime < oneMonthInMs;
          if (isLoggedInLastMonth) {
            HistoryHelper.push({
              pathname: "/login",
              search: Utils.qs({
                shopifyCode: code,
                shopifyInt: true,
                shopifyConnDone: isConnectedToMyProject,
                shopifyConnId: checkStatus?.connected_project_id,
              }),
            });
          } else {
            HistoryHelper.push({
              pathname: "/register",
              search: Utils.qs({
                shopifyCode: code,
                shopifyInt: true,
                shopifyConnDone: isConnectedToMyProject,
                shopifyConnId: checkStatus?.connected_project_id,
              }),
            });
          }
        });
      }
    }
  }, [
    countDown,
    loading,
    userInfo,
    code,
    isConnectedToAnotherProject,
    isConnectedToMyProject,
    checkStatus?.connected_project_id,
  ]);
  useEffect(() => {
    if (loading) return;
    const interval = setInterval(() => {
      setTreeDotsCount((prev) => {
        if (prev === 2) {
          return 0;
        }
        return prev + 1;
      });
    }, 500);

    return () => clearInterval(interval);
  }, [loading]);

  const threeDoComp = (
    <Box component="span">
      <Box component="span" visibility={treeDotsCount >= 0 ? "visible" : "hidden"}>
        .
      </Box>
      <Box component="span" visibility={treeDotsCount >= 1 ? "visible" : "hidden"}>
        .
      </Box>
      <Box component="span" visibility={treeDotsCount >= 2 ? "visible" : "hidden"}>
        .
      </Box>
    </Box>
  );

  return (
    <StyledDiv>
      <Box alignItems="center" display="flex" flexDirection={"column"} height="100%" justifyContent="center">
        {!!code &&
          (isConnectedToAnotherProject ? (
            <>
              <WarningAmber className="icon" color="error" />
              <div className="message">
                {t("onboarding.integrationAlreadyConnected").format(
                  checkStatus?.project_name,
                  checkStatus?.company_name,
                  checkStatus?.company_owner_name
                )}
              </div>
            </>
          ) : (
            <>
              <Done className="icon" color="success" />
              <div className="message">
                {!!isConnectedToMyProject && t("onboarding.shopifyCallback.redirectingToDashboardTitle")}
                {!isConnectedToMyProject &&
                  !!userInfo?.id &&
                  t("onboarding.shopifyCallback.redirectingToOnboardingTitle")}
                {!isConnectedToMyProject && !userInfo?.id && t("onboarding.shopifyCallback.redirectingToRegisterTitle")}
              </div>
              <div className="message message-countdown">
                {t("onboarding.integrationCallbackPage.redirecting").format(countDown)}
                {threeDoComp}
              </div>
            </>
          ))}
        {!code && (
          <>
            <WarningAmber className="icon" color="error" />
            <div className="message">{t("onboarding.integrationCallbackPage.error")}</div>
          </>
        )}
      </Box>
    </StyledDiv>
  );
};

export default ShopifyCallback;
